import { openDialog } from '@patrianna/dialog-manager'
import { useMediaQuery } from '@patrianna/shared-hooks'
import { getCookie, nextTick } from '@patrianna/shared-utils'
import { getTranslationFromJson } from '@patrianna/shared-utils/prismic'
import { useRouter } from 'next/navigation'
import { type FC } from 'react'

import { IS_BRAND_PULSZ } from 'config/brands'
import ROUTES from 'config/routes.json'
import { GoldCoinAnimated } from 'containers/Menu/AccountInfo/GoldCoinAnimated'
import { SweepsStakeAnimated } from 'containers/Menu/AccountInfo/SweepsStakeAnimated'
import { VipLevelAnimated } from 'containers/Menu/AccountInfo/VipLevelAnimated'
import usePaymentRedeemRestrictionsFunction from 'hooks/payments/usePaymentRedeemRestrictionsFunction'
import content from 'prismic-translates/common.json'
import { trackSPPlacementInteracted } from 'src/analyticActions/common/trackPaymentSnowplow'
import Button from 'src/components/Button'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { getIsAcceptedRulesForScUser, sweepstakeEnabledSelector } from 'store/modules/appConfig/selectors'
import { getKycStatusSelector } from 'store/modules/kyc/selectors'
import { redeemFlowStart } from 'store/modules/redeem/actions'
import { openShopHandler } from 'store/modules/shop/actions'
import { getAcceptedSrVersionSelector } from 'store/modules/user/selectors'

import { useShouldRedirectToOtp } from '../utils'

import classes from './styles.module.scss'

const t = getTranslationFromJson([content])

const AccountInfo: FC<{ handleTrackEvent: (targetName: string) => void; hideMenuCallback?: () => void }> = ({
  handleTrackEvent,
  hideMenuCallback,
}) => {
  const router = useRouter()
  const isMobile = useMediaQuery('xs')
  const dispatch = useAppDispatch()
  const kycStatus = useAppSelector(getKycStatusSelector)
  const secondStep = ['id_confirmed', 'doc_declined'].includes(kycStatus)

  const isAcceptedRulesForScUser = useAppSelector(getIsAcceptedRulesForScUser)
  const isScEnabled = useAppSelector(sweepstakeEnabledSelector)
  const isOtpRedirect = useShouldRedirectToOtp()
  const acceptedSrVersion = useAppSelector(getAcceptedSrVersionSelector)
  const switchCurrencyModalCookie = Boolean(getCookie('SWITCH_CURRENCY_MODAL', document.cookie))
  const userShouldAcceptScRules = isScEnabled && (!acceptedSrVersion || switchCurrencyModalCookie)

  const handleRedeemFlowStart = () => {
    if (secondStep) {
      openDialog({ dialogName: 'SUBMIT_DOCUMENTS_DIALOG', dialogProps: { verificationFrom: 'redeem' } })
    } else {
      dispatch(redeemFlowStart(router.push))
    }
  }

  const handleClickGetCoins = () => {
    nextTick().then(() => {
      if (isOtpRedirect) {
        window.location.href = ROUTES.OTP
      }

      if (userShouldAcceptScRules) {
        window.location.href = ROUTES.HOME
      }

      trackSPPlacementInteracted({
        paymentFlow: 'coin_store',
        placementName: 'cta_button',
        interactionName: t('get_coins'),
        placementTrigger: 'click_menu_button',
      })
      dispatch(openShopHandler(isMobile, router.push))
      handleTrackEvent('get_coins')
    })
  }

  const handleClickRedeemButton = () => {
    nextTick().then(() => {
      if (isOtpRedirect) {
        window.location.href = ROUTES.OTP
      }

      if (userShouldAcceptScRules) {
        window.location.href = ROUTES.HOME
      }

      redeemFlowStartWithRestrictions()
      handleTrackEvent('redeem')
    })
  }

  const redeemFlowStartWithRestrictions = usePaymentRedeemRestrictionsFunction(handleRedeemFlowStart, 'redeem')

  return (
    <div className={classes.root} data-test='menu-account-info'>
      {!IS_BRAND_PULSZ && <VipLevelAnimated hideMenuCallback={hideMenuCallback} />}

      <GoldCoinAnimated />
      {isScEnabled && <SweepsStakeAnimated />}
      <div className={classes.buttons}>
        <Button
          dataTestId='menu-items-get-coins-button'
          onClick={handleClickGetCoins}
          className={classes.button}
          disabled={!isAcceptedRulesForScUser}
          variant={secondStep && isScEnabled ? 'outlined' : null}
        >
          {t('get_coins')}
        </Button>
        {isScEnabled ? (
          <Button
            dataTestId='menu-redeem-button'
            onClick={handleClickRedeemButton}
            className={classes.button}
            variant={!secondStep ? 'outlined' : null}
            disabled={!isAcceptedRulesForScUser}
          >
            {secondStep ? t('verify') : t('redeem')}
          </Button>
        ) : null}
      </div>
    </div>
  )
}

export default AccountInfo
